import { useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAssessCancer } from '@hooks/register/screening/use-assess-cancer';
import { useLocalize } from '@hooks/use-localize';
import { useStepForm } from '@hooks/use-step-form';
import { useSession } from '@hooks/security/use-session';
import { assessCancerSchema } from '@views/register/screening/assess-cancer.schema';
import { asString, asStringBool } from '@/utils/parse';
import { DashRegistrationStep, screeningLinkMap } from '@/types/dashboard';
import type { IAssessCancerFormValues, IAssessCancerModel, IAssessCancerSubmitResult } from '@/types/screening/assess-cancer';
import PageLoader from '@components/page-loader';
import StepFormLayout from '@components/step-form-layout';
import FieldError from '@components/field-error';

// Nkr.Model.Enums.MedicalAssessments.CancerType (value → default English label).
const CANCER_TYPE_OPTIONS: { value: number; label: string }[] = [
    { value: 5, label: 'Bladder' },
    { value: 10, label: 'Bone' },
    { value: 15, label: 'Brain' },
    { value: 20, label: 'Breast' },
    { value: 25, label: 'Colon' },
    { value: 26, label: 'Esophageal' },
    { value: 30, label: 'Kidney' },
    { value: 35, label: 'Liver' },
    { value: 40, label: 'Lung' },
    { value: 45, label: 'Melanoma' },
    { value: 48, label: 'Melanoma in situ' },
    { value: 50, label: 'Non-Hodgkin Lymphoma' },
    { value: 55, label: 'Pancreatic' },
    { value: 60, label: 'Prostate' },
    { value: 65, label: 'Skin' },
    { value: 70, label: 'Thyroid' },
    { value: 100, label: 'Other' },
];

// Nkr.Model.Enums.MedicalAssessments.RemissionStatus (value → default English label).
const REMISSION_OPTIONS: { value: number; label: string }[] = [
    { value: 0, label: 'Less than 1 year' },
    { value: 1, label: '1 year' },
    { value: 2, label: '2 years' },
    { value: 3, label: '3 years' },
    { value: 4, label: '4 years' },
    { value: 5, label: '5 years' },
    { value: 6, label: '6 years' },
    { value: 7, label: '7 years' },
    { value: 8, label: '8 years' },
    { value: 9, label: '9 years' },
    { value: 10, label: '10 years' },
    { value: 11, label: '11 years' },
    { value: 12, label: '12 years' },
    { value: 13, label: '13 years' },
    { value: 14, label: '14 years' },
    { value: 15, label: '15+ years' },
];

const AssessCancer = () => {
    const { registrationId, editKey } = useSession();
    const { model, isLoading, error, submit, isSubmitting, submitError } = useAssessCancer(registrationId, editKey);

    return (
        <PageLoader isLoading={isLoading} error={error}>
            <AssessCancerContent
                model={model}
                isSubmitting={isSubmitting}
                submitError={submitError}
                onSubmit={submit}
            />
        </PageLoader>
    );
};

interface IAssessCancerContentProps {
    model: IAssessCancerModel;
    isSubmitting: boolean;
    submitError: string | null;
    onSubmit: (values: IAssessCancerFormValues) => Promise<IAssessCancerSubmitResult>;
}

const AssessCancerContent = ({ model, isSubmitting, submitError, onSubmit }: IAssessCancerContentProps) => {
    const t = useLocalize(model.languageData);

    const form = useStepForm<IAssessCancerFormValues, IAssessCancerSubmitResult>({
        resolver: zodResolver(assessCancerSchema),
        defaultValues: {
            hasHadCancer: asStringBool(model.hasHadCancer),
            cancers:
                model.cancers.length > 0
                    ? model.cancers.map((c) => ({
                        cancerType: asString(c.cancerType),
                        remissionStatus: asString(c.remissionStatus),
                    }))
                    : [{ cancerType: '', remissionStatus: '' }],
        },
        submit: onSubmit,
        linkMap: screeningLinkMap(model.isFemale, model.isLiver),
    });

    const {
        control,
        register,
        watch,
        formState: { errors },
    } = form;

    const { fields, append, remove } = useFieldArray({ control, name: 'cancers' });

    const showStatus = watch('hasHadCancer') === '1';

    const deleteCancer = t('DashLabel_DeleteCancer', 'Delete Cancer');

    return (
        <StepFormLayout
            kind="screening"
            model={model}
            step={DashRegistrationStep.AssessCancer}
            title={t('DashLabel_ProgressCancer', 'Cancer')}
            t={t}
            form={form}
            isSubmitting={isSubmitting}
            submitError={submitError}
        >
            <div className="form-group">
                <label className="control-label col-xs-12">
                    {t(
                        'DashLabel_HaveYouEverBeenDiagnosedWithACancerIncludingSkinCancer',
                        'Have you ever been diagnosed with a cancer (including skin cancer)',
                    )}
                    ?<FieldError message={errors.hasHadCancer?.message} />
                </label>
                <div className="col-xs-12" style={{ marginLeft: 10 }}>
                    <label className="radio-inline">
                        <input type="radio" value="1" {...register('hasHadCancer')} />{' '}
                        {t('Word_Yes', 'Yes')}
                    </label>
                    <label className="radio-inline">
                        <input type="radio" value="0" {...register('hasHadCancer')} />{' '}
                        {t('Word_No', 'No')}
                    </label>
                </div>
            </div>

            {showStatus && (
                <div className="sub-question" id="status">
                    <div className="form-group">
                        <div className="col-xs-12" style={{ marginLeft: 10 }}>
                            <div className="help-block">
                                {t('DashLabel_PleaseListAllCancers', 'Please list all cancers')}
                            </div>

                            <div className="row hidden-xs">
                                <div className="col-xs-4 col-sm-4">
                                    {t('DashLabel_WhatTypeOfCancer', 'What type of cancer')}?
                                </div>
                                <div className="col-xs-4 col-sm-4">
                                    {t(
                                        'DashLabel_HowLongHaveYouBeenInRemission',
                                        'How long have you been in remission',
                                    )}
                                    ?
                                </div>
                            </div>

                            {fields.map((field, index) => (
                                <div className="row row-margined" key={field.id}>
                                    <div className="col-xs-12 col-sm-4">
                                        <select
                                            className="form-control"
                                            {...register(`cancers.${index}.cancerType`)}
                                        >
                                            <option value="">
                                                {t('DashLabel_PleaseSelect', 'Please Select')}
                                            </option>
                                            {CANCER_TYPE_OPTIONS.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {t(
                                                        `Enum_CancerType_${option.value}`,
                                                        option.label,
                                                    )}
                                                </option>
                                            ))}
                                        </select>
                                        <FieldError
                                            message={
                                                errors.cancers?.[index]?.cancerType?.message
                                            }
                                        />
                                    </div>
                                    <div className="col-xs-12 col-sm-4">
                                        <select
                                            className="form-control"
                                            {...register(`cancers.${index}.remissionStatus`)}
                                        >
                                            <option value="">
                                                {t('DashLabel_PleaseSelect', 'Please Select')}
                                            </option>
                                            {REMISSION_OPTIONS.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {t(
                                                        `Enum_RemissionStatus_${option.value}`,
                                                        option.label,
                                                    )}
                                                </option>
                                            ))}
                                        </select>
                                        <FieldError
                                            message={
                                                errors.cancers?.[index]?.remissionStatus
                                                    ?.message
                                            }
                                        />
                                    </div>
                                    <div className="col-xs-12 col-sm-4">
                                        {index > 0 && (
                                            <button
                                                type="button"
                                                className="btn btn-danger btn-delete-entry"
                                                aria-label={deleteCancer}
                                                title={deleteCancer}
                                                onClick={() => remove(index)}
                                            >
                                                <span className="glyphicon glyphicon-trash" aria-hidden="true"></span>
                                            </button>
                                        )}
                                    </div>
                                    <div className="visible-xs">&nbsp;</div>
                                </div>
                            ))}

                            <div className="row">
                                <div className="col-xs-12" style={{ marginLeft: 0 }}>
                                    <button
                                        type="button"
                                        className="btn btn-outline-primary"
                                        onClick={() =>
                                            append({ cancerType: '', remissionStatus: '' })
                                        }
                                    >
                                        <span className="glyphicon glyphicon-plus-sign"></span>{' '}
                                        {t('DashLabel_AddCancer', 'Add Cancer')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </StepFormLayout>
    );
};

export default AssessCancer;
